import type { GroupType } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import { Switch } from '@lms/ui/switch';
import { Textarea } from '@lms/ui/textarea';
import { FileUpIcon, PlusIcon } from 'lucide-react';
import { type ChangeEvent, type FormEvent, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { SubmitButton } from '@/components/submit-button';
import type { Site } from '@/features/branding/brand-provider';
import { addGroupSchema } from '@/features/groups/actions.schema';
import { addGroup, batchAddGroups } from '@/features/groups/groups-api';
import { parseCsv, readTextFile } from '@/lib/csv';
import { readXlsxRows } from '@/lib/xlsx';

function AddGroupForm({
  site,
  groupType,
  submitLabel,
  onSuccess,
}: {
  site: Site;
  groupType: GroupType;
  submitLabel: string;
  onSuccess: () => void;
}) {
  const t = useTranslations();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);

    const submission = addGroupSchema.safeParse({ name, groupType });
    if (!submission.success) {
      setError(t('admin.name'));
      return;
    }

    setPending(true);
    try {
      const result = await addGroup({
        name: submission.data.name,
        groupType: submission.data.groupType,
        locationId: site.id,
      });
      if (result.ok) {
        onSuccess();
      } else {
        setError(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label>{t('admin.name')}</Label>
        <Input
          className="input input-bordered flex-grow"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>
      {error && <div className="text-destructive text-sm">{error}</div>}
      <SubmitButton label={submitLabel} pending={pending} />
    </form>
  );
}

/**
 * Names from rows in the shape the group cards export: the first column is the
 * name. A header row is dropped when it's the column title or has a
 * non-numeric second cell (data rows carry a course count there).
 */
function namesFromRows(allRows: string[][], nameHeader: string): string[] {
  const rows = allRows.filter((r) => r.some((c) => c.trim() !== ''));
  const [first] = rows;
  if (
    first &&
    (first[0].trim().toLowerCase() === nameHeader.toLowerCase() ||
      (first.length > 1 &&
        first[1].trim() !== '' &&
        Number.isNaN(Number(first[1]))))
  ) {
    rows.shift();
  }
  return rows.map((r) => r[0].trim()).filter((n) => n !== '');
}

function BatchAddGroupsForm({
  site,
  groupType,
  what,
  whatPlural,
  existingNames,
  onSuccess,
  onPartial,
}: {
  site: Site;
  groupType: GroupType;
  what: string;
  whatPlural: string;
  existingNames: string[];
  onSuccess: () => void;
  /** A failed batch may still have created some groups; refresh the list. */
  onPartial: () => void;
}) {
  const t = useTranslations();
  const fileInput = useRef<HTMLInputElement>(null);
  const [names, setNames] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  // The backend inserts one by one and stops at the first duplicate (names are
  // unique per site), so drop existing names and repeats up front.
  const existing = new Set(existingNames);
  const seen = new Set<string>();
  const toAdd: string[] = [];
  const skippedSet = new Set<string>();
  for (const line of names.split('\n')) {
    const name = line.trim();
    if (!name) continue;
    if (existing.has(name) || seen.has(name)) skippedSet.add(name);
    else toAdd.push(name);
    seen.add(name);
  }
  const skipped = [...skippedSet];

  const onImport = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setError(undefined);
    let rows: string[][];
    try {
      rows = /\.xlsx$/i.test(file.name)
        ? await readXlsxRows(file)
        : parseCsv(await readTextFile(file));
    } catch {
      setError(t('admin.import_unreadable'));
      return;
    }
    const imported = namesFromRows(rows, what);
    if (imported.length === 0) {
      setError(t('admin.import_empty'));
      return;
    }
    setNames((prev) =>
      [prev.trimEnd(), ...imported].filter((l) => l !== '').join('\n'),
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setPending(true);
    try {
      const result = await batchAddGroups({
        names: toAdd.join('\n'),
        grouptype: groupType,
        locationId: site.id,
      });
      if (result.ok) {
        onSuccess();
      } else {
        setError(result.message);
        onPartial();
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-end justify-between gap-2">
          <Label htmlFor="batch-names">{t('admin.batch_hint')}</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInput.current?.click()}
          >
            <FileUpIcon className="mr-2 h-4 w-4" />
            {t('admin.import_file')}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            className="hidden"
            onChange={onImport}
          />
        </div>
        <Textarea
          id="batch-names"
          value={names}
          onChange={(e) => setNames(e.target.value)}
          placeholder={`${`${what}\n`.repeat(3)}...`}
          rows={8}
          autoFocus
        />
        {skipped.length > 0 && (
          <p className="text-muted-foreground text-sm">
            {t('admin.batch_skipped', { count: skipped.length })}{' '}
            <span className="text-foreground">{skipped.join(', ')}</span>
          </p>
        )}
      </div>
      {error && <div className="text-destructive text-sm">{error}</div>}
      <SubmitButton
        label={t('admin.add', {
          what: `${toAdd.length} ${(toAdd.length === 1 ? what : whatPlural).toLowerCase()}`,
        })}
        pending={pending}
        disabled={toAdd.length === 0}
      />
    </form>
  );
}

/**
 * "Add" button + dialog for one group card. The card decides the group type,
 * so the dialog only asks for a name — or, in batch mode, a list of names
 * (typed or imported from an .xlsx or .csv file).
 */
export default function AddGroupDialog({
  site,
  groupType,
  what,
  whatPlural,
  existingNames,
  onCreated,
}: {
  site: Site;
  groupType: GroupType;
  /** Singular noun for the card's groups, e.g. "Butik" or "Leverantör". */
  what: string;
  /** Plural of `what`, e.g. "Butiker". */
  whatPlural: string;
  /** Names already taken at this site (unique across group types). */
  existingNames: string[];
  onCreated: () => void;
}) {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const title = t('admin.add', { what: what.toLowerCase() });

  const onSuccess = () => {
    setOpen(false);
    onCreated();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <PlusIcon className="mr-1 h-4 w-4" />
          {title}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Switch
            id="batch-mode"
            checked={batchMode}
            onCheckedChange={setBatchMode}
          />
          <Label htmlFor="batch-mode">{t('admin.batch_mode')}</Label>
        </div>
        {batchMode ? (
          <BatchAddGroupsForm
            site={site}
            groupType={groupType}
            what={what}
            whatPlural={whatPlural}
            existingNames={existingNames}
            onSuccess={onSuccess}
            onPartial={onCreated}
          />
        ) : (
          <AddGroupForm
            site={site}
            groupType={groupType}
            submitLabel={title}
            onSuccess={onSuccess}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
